import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Bot } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/brand/Logo";

function Shell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between tdw-hero-gradient text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 tdw-circuit opacity-50" />
        <div className="relative"><Logo variant="wordmark-light" /></div>
        <div className="relative">
          <Bot className="h-12 w-12 text-tech mb-4" />
          <h2 className="font-heading text-3xl font-bold">Intelligent Business Assistance, 24/7</h2>
          <p className="mt-3 text-slate-300 max-w-sm">Manage leads, requests, appointments, and your Nexus AI assistant — all in one place.</p>
        </div>
        <p className="relative text-xs text-slate-400 font-mono">Powered by Thomason Digital Works</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex justify-center"><Logo /></div>
          <h1 className="font-heading text-2xl font-bold text-navy">{title}</h1>
          <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

const input = "w-full h-11 px-3 rounded-lg border border-input bg-white focus:outline-none focus:ring-2 focus:ring-electric text-sm";

export function OwnerLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const u = await login(email, password); const dest = u.role === "customer" ? "/portal" : u.role === "super_admin" ? "/platform" : "/dashboard"; nav(dest); toast.success("Welcome back!"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Owner & Staff Login" subtitle="Sign in to the TDW Nexus dashboard">
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="owner-login-form">
        <input className={input} type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="login-email" />
        <input className={input} type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="login-password" />
        <Button type="submit" disabled={loading} className="w-full bg-electric hover:bg-tech" data-testid="login-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}</Button>
        <Link to="/forgot-password" className="block text-center text-sm text-electric">Forgot password?</Link>
        <Link to="/" className="block text-center text-xs text-slate-400">← Back to website</Link>
      </form>
    </Shell>
  );
}

export function CustomerLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await login(email, password); nav("/portal"); toast.success("Welcome back!"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Client Login" subtitle="Access your service requests, appointments & more">
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="customer-login-form">
        <input className={input} type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="login-email" />
        <input className={input} type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="login-password" />
        <Button type="submit" disabled={loading} className="w-full bg-electric hover:bg-tech" data-testid="login-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}</Button>
        <p className="text-center text-sm text-slate-500">No account? <Link to="/customer/register" className="text-electric">Create one</Link></p>
        <Link to="/" className="block text-center text-xs text-slate-400">← Back to website</Link>
      </form>
    </Shell>
  );
}

export function CustomerRegister() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await register(form); nav("/portal"); toast.success("Account created!"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Create your account" subtitle="Track your projects, repairs, and invoices">
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="customer-register-form">
        <input className={input} placeholder="Full name" value={form.name} onChange={e => set("name", e.target.value)} required data-testid="reg-name" />
        <input className={input} type="email" placeholder="Email" value={form.email} onChange={e => set("email", e.target.value)} required data-testid="reg-email" />
        <input className={input} placeholder="Phone (optional)" value={form.phone} onChange={e => set("phone", e.target.value)} data-testid="reg-phone" />
        <input className={input} type="password" placeholder="Password (min 6 chars)" value={form.password} onChange={e => set("password", e.target.value)} required data-testid="reg-password" />
        <Button type="submit" disabled={loading} className="w-full bg-electric hover:bg-tech" data-testid="reg-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}</Button>
        <p className="text-center text-sm text-slate-500">Have an account? <Link to="/customer/login" className="text-electric">Sign in</Link></p>
      </form>
    </Shell>
  );
}
